// eslint-disable-next-line no-restricted-imports -- RVSelect is the wrapper component that needs to import Select from antd
import { Select } from "antd";
import { clsx } from "clsx";

import { RVIcon } from "../RVIcon/RVIcon";

import type { SelectProps } from "antd";

import styles from "./RVSelect.module.scss";

export type RVSelectProps = SelectProps & {
    /** Optional content rendered in a bordered panel appended to the right of the select field */
    rightAction?: React.ReactNode;
    /** Additional CSS class name(s) applied to the outer container div */
    containerClassName?: string;
};

/**
 * RVSelect
 *
 * A standardized select component that wraps Ant Design's Select with consistent
 * Regal styling, including a custom chevron suffix icon and an optional right action slot.
 *
 * - Replaces the default antd chevron with `RVIcon icon="chevron-down"`
 * - Supports an optional `rightAction` slot rendered as a bordered panel to the right
 * - Passes through all antd `SelectProps`
 *
 * @example
 * // Basic usage
 * <RVSelect placeholder="Select an option" options={[{ value: "a", label: "Option A" }]} />
 *
 * @example
 * // With right action
 * <RVSelect
 *   placeholder="Select"
 *   rightAction={<RVButton type="link">Edit</RVButton>}
 * />
 *
 * @param {RVSelectProps} props - Component props
 * @returns {JSX.Element}
 */
export function RVSelect({ rightAction, containerClassName, ...props }: RVSelectProps): JSX.Element {
    return (
        <div className={clsx(styles.selectContainer, containerClassName)} data-testid="select-container">
            <Select
                data-testid="select-component"
                suffixIcon={<RVIcon icon="chevron-down" />}
                {...props}
                className={clsx(props.className, {
                    [styles.hasRightAction]: !!rightAction,
                })}
            />
            {rightAction && <div className={styles.rightActionContainer}>{rightAction}</div>}
        </div>
    );
}
